/****************************************************************************
 ** @license
 ** This demo file is part of yFiles for HTML 2.2.0.1.
 ** Copyright (c) 2000-2019 by yWorks GmbH, Vor dem Kreuzberg 28,
 ** 72070 Tuebingen, Germany. All rights reserved.
 **
 ** yFiles demo files exhibit yFiles for HTML functionalities. Any redistribution
 ** of demo files in source code or binary form, with or without
 ** modification, is not permitted.
 **
 ** Owners of a valid software license for a yFiles for HTML version that this
 ** demo is shipped with are allowed to use the demo source code as basis
 ** for their own yFiles for HTML powered applications. Use of such programs is
 ** governed by the rights and conditions as set out in the yFiles for HTML
 ** license agreement.
 **
 ** THIS SOFTWARE IS PROVIDED ''AS IS'' AND ANY EXPRESS OR IMPLIED
 ** WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF
 ** MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN
 ** NO EVENT SHALL yWorks BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 ** SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED
 ** TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 ** PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 ** LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
 ** NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 ** SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 **
 ***************************************************************************/
/**
 * Provides helper methods for file saving.
 */

 //a.click() doesn't work for all browsers
function click (node) {
  try   {
    node.dispatchEvent(new MouseEvent("click"));
  } 
  catch (e)   {
    var evt = document.createEvent("MouseEvents");
    evt.initMouseEvent("click", true, true, window, 0, 0, 0, 80, 20, false, false, false, false, 0, null);
    node.dispatchEvent(evt);
  }
}

export default class FileSaveSupport {
  /**
   * Saves the file to the file system using the HTML5 File download or
   * the proprietary msSaveOrOpenBlob function in Internet Explorer.
   *
   * @param {string} fileContent The file contents to be saved.
   * @param {string} fileName The default filename for the downloaded file.
   * @return {Promise} A promise which resolves when the save operation is complete.
   */
  static save(fileContent, fileName) {
    return new Promise((resolve, reject) => {
      // extract file format
      const format = fileName.split(".")[1].toLowerCase();

      if (FileSaveSupport.isFileConstructorAvailable()) {
        if (
          format === "txt" ||
          format === "json" ||
          format === "svg" ||
          format === "graphml"
        ) {
          let mimeType = "";
          switch (format) {
            case "txt":
            case "json":
            default:
              mimeType = "text/plain";
              break;
            case "svg":
              mimeType = "image/svg+xml";
              break;
            case "graphml":
              mimeType = "application/xml";
              break;
          }

          // workaround for supporting non-binary data
          fileContent = URL.createObjectURL(
            new Blob([fileContent], { type: mimeType })
          );
        }

        const aElement = document.createElement("a");
        aElement.setAttribute("href", fileContent);
        aElement.setAttribute("download", fileName);
        aElement.style.display = "none";
        document.body.appendChild(aElement);
        setTimeout(function () { click(aElement) }, 0);
        setTimeout(function () { URL.revokeObjectURL(aElement.href) }, 4E4); // 40s
        setTimeout(function () { document.body.removeChild(aElement) }, 1000);
        document.body.removeChild(aElement);

        resolve("File saved successfully");
        return;
      }
      if (FileSaveSupport.isMsSaveAvailable()) {
        let blob;
        if (fileContent.startsWith("data:")) {
          const dataUrlParts = fileContent.split(",");
          const bString = window.atob(dataUrlParts[1]);
          const byteArray = [];
          for (let i = 0; i < bString.length; i++) {
            byteArray.push(bString.charCodeAt(i));
          }
          // For the options, extract the mime type from the Data URL
          blob = new Blob([new Uint8Array(byteArray)], {
            type: dataUrlParts[0].match(/:(.*?);/, "")[1]
          });
        } else {
          blob = new Blob([fileContent]);
        }

        if (window.navigator.msSaveOrOpenBlob(blob, fileName)) {
          resolve("File saved successfully");
        } else {
          reject(
            new Error("File save failed: A failure occurred during saving.")
          );
        }
        return;
      }
      reject(
        new Error(
          "File save failed: Save operation is not supported by the browser."
        )
      );
    });
  }

  static isFileConstructorAvailable() {
    // Test whether required functions exist
    if (
      typeof window.URL !== "function" ||
      typeof window.Blob !== "function" ||
      typeof window.File !== "function"
    ) {
      return false;
    }
    // Test whether the constructor works as expected
    try {
      // eslint-disable-next-line no-new
      new File(["Content"], "fileName", {
        type: "image/png",
        lastModified: Date.now()
      });
    } catch (ignored) {
      return false;
    }
    // Everything is available
    if ( navigator.userAgent.indexOf('Edge') >= 0 )
    {
      //LEGACY EDGE SUCKS! DESPITE THIS FUNCTION WOULD RETURN TRUE, CAN'T SAVE SHIT WITH MODERN METHODS!
      return false; 
    }
    else
    { 
      return true;
    }
    
  }

  /**
   * Returns whether the MS Internet Explorer specific save technique is available.
   * This works in IE 10+. See the related demo for more details.
   * for more details.
   * @return {boolean}
   */
  static isMsSaveAvailable() {
    return (
      typeof window.Blob === "function" &&
      typeof window.navigator.msSaveOrOpenBlob === "function"
    );
  }
}
